<template>
    <div>

        <fullscreen-loading v-if="loading" />

        <div v-if="showSubmittedModal()" class="ceremony_submitted">
            <div class="card" style="max-width: 600px;">
                <div class="card-body">
                    <div class="pt-2 pb-3" v-if="ceremony">
                        <h5>
                            {{ $t('blocks.submitted_header', { type: ceremony.translated_type }) }}
                        </h5>
                        <p class="text-muted mb-0">
                            {{ $t('blocks.submitted_message', { type: ceremony.translated_type }) }}
                        </p>
                    </div>

                    <download-ceremony
                        v-if="!ceremony.cleaned_up_at && ceremony.state_id !== 1"
                        :tooltip="$t('blocks.download_tooltip')"
                        :with-text="true"
                        :ceremony="ceremony"
                    />

                    <button type="button" class="btn btn-company btn-sm btn-icon rounded-pill float-right mr-2"
                            @click="previewCeremony">
                        <span class="btn-inner--icon">
                            <icon name="play"></icon>
                        </span> <span class="btn-inner--text">{{ $t('blocks.preview_button') }}</span>
                    </button>

                    <a :href="'/api/ceremonies/code/' + ceremony.code  + '/pdf-preview'" target="_blank" type="button"
                       class="btn btn-company btn-sm btn-icon rounded-pill float-right mr-2"
                       v-tooltip="$t('blocks.download_pdf_tooltip', {type: ceremony.translated_type.toLowerCase()})">
                            <span class="btn-inner--icon">
                                <icon name="book"></icon>
                            </span> <span class="btn-inner--text">{{ $t('blocks.pdf_button') }}</span>
                    </a>
                </div>
            </div>
        </div>

        <fullscreen-slider :files="previewFiles" :file-index="previewFileIndex" :ceremony-code="code"
                           @clear="clearFilePreview" />

        <div class="timeline-wrapper">
            <div v-if="ceremony && fixedBlock()" class="fixed-block-wrapper"
                 :class="fixedBlockCollapsed ? 'collapsed' : null" @click="fixedBlockCollapsed = false">
                <div class="card mt-3">
                    <div class="card-body position-relative">
                        <h6>{{ $t('blocks.fixed_photo') }}</h6>
                        <div v-if="getFixedPhoto()" class="pb-2">
                            <file-block
                                v-if="fixedBlock().subblocks[0].id"
                                :ceremony-code="code"
                                :block="fixedBlock().subblocks[0]"
                                :file="getFixedPhoto()"
                                :can-grab="false"
                                :can-edit="!fixedBlockCollapsed"
                                :auto-ratio="true"
                                :collapse="fixedBlockCollapsed"
                                @unlink="removeFixedPhoto()"
                                @is-editing="isEditingFile = $event"
                                @save="$store.dispatch('updateCeremony', $route.params.code)"
                                @files-updated="filesUpdated(fixedBlock().subblocks[0], $event)"
                                @file-state-changed="addToPreviewIfNotExists"
                                @click="!fixedBlockCollapsed ? showFilePreview(getFilesForType(fixedBlock().subblocks[0]), getFixedPhoto()) : null">
                                <img style="width: 100%"
                                     :src="getFixedPhoto().thumbnail_path.replace(/\/:code\//g, '/' + $route.params.code + '/')" />
                            </file-block>
                            <button class="btn btn-secondary btn-sm rounded-pill btn-icon btn-compact"
                                    @click="toggleSubBlockModal(fixedBlock().subblocks[0])">
                                {{ $t('blocks.photo_settings') }}
                            </button>

                            <a
                                v-if="ceremony.company.block_rendering_enabled"
                                :href="canDownloadBlockRender(fixedBlock()) ? fixedBlock().render_url : null"
                                @click.stop
                                :download="fixedBlock().name + '.mp4'"
                                target="_blank"
                                class="mr-2 action-item"
                                :class="{ 'disabled-block-download': !canDownloadBlockRender(fixedBlock()) }"
                                v-tooltip="canDownloadBlockRender(fixedBlock()) ? $t('blocks.download_block_as_video') : $t('blocks.download_block_as_video_not_available')"
                            >
                                <icon name="download" />
                            </a>
                        </div>

                        <div v-if="!getFixedPhoto()">
                            <small class="d-block upload-fixed-photo-text">
                                {{ $t('blocks.upload_fixed_photo') }}
                            </small>
                            <dropzone-upload
                                v-if="!getFixedPhoto() && !fixedBlockCollapsed"
                                :url="`/api/ceremonies/code/${code}/files/${fixedBlock().subblocks[0].id}`"
                                :extensions_text="'.png, .jpg, .jpeg, .gif, .heic, .webp'"
                                :icon-only="true"
                                :icon_text="$t('blocks.upload_photo_button')"
                                :unique_number_per_dropzone="12"
                                :max_files="1"
                                :max_size_mb_per_file="15"
                                :bestand="[]"
                                :unique_string_per_dropzone="'fixed_photo'"
                                @uploaded="fixedphotoUploaded"
                            >
                            </dropzone-upload>
                        </div>

                        <BlockTimelineRenderStateIcon
                            v-if="ceremony.company.block_rendering_enabled"
                            class="fixed-image-block-render-state"
                            :block="fixedBlock()"
                            color="#273444"
                        />
                    </div>
                </div>
            </div>

            <div>
                <block-timeline @show_block_modal="createNewBlock"
                                @block_selected="blockSelected"
                                @block_deleted="toggleDeleteModal"
                                v-if="code && timelineCeremony"
                                :selected_block="selected_block"
                                :ceremony="timelineCeremony"
                ></block-timeline>
            </div>

        </div>

        <preview />

        <!-- start no block selected -->
        <div class="alert alert-group alert-secondary fade show alert-icon" role="alert"
             v-if="!selected_block && !fullscreenLoading && !loading" style="display: inline-block;">
            <div class="alert-group-prepend">
                <span class="alert-group-icon text-">
                    <icon name="info-circle"></icon>
                </span>
            </div>
            <div class="alert-content" style="display: inline-block;">
                <strong></strong> {{ noBlockSelectedText }}
            </div>
        </div>
        <!-- end no block selected -->

        <div v-if="selected_block && selected_block.id" class="row" :class="{ 'embed-block-content-wrapper': embed }">
            <div class="col-12 col-lg-3">
                <div style="position: absolute; z-index:50;left:5px;top:-10px;">
                    <span class="badge badge-info" v-if="isInloopUitloopBlock(block)">{{ companySettings?.inloop_uitloop_type_text?.length ? companySettings?.inloop_uitloop_type_text : $t('blocks.background_music') }}</span>
                    <span class="badge badge-info" v-if="isCondolenceBlock(block)">{{ $t('blocks.condolence_block') }}</span>
                    <span class="badge badge-info" v-if="isMediaBlock(block)">{{ $t('blocks.media_block') }}</span>
                    <span class="badge badge-dark" v-if="isSpeakerBlock(block)">{{ $t('blocks.speaker_block') }}</span>
                    <span class="badge badge-dark" v-if="isCallBlock(block)">{{ $t('blocks.call_block') }}</span>
                </div>
                <div class="card mb-4">

                    <!-- Card body -->
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <div class="icon icon-sm">
                                    <img :src="getBlockImage(block)" class="avatar rounded-circle">
                                </div>
                            </div>
                            <div class="text-right">
                                <div class="actions mr-n2">
                                    <div class="dropdown action-item">
                                        <a
                                            v-if="ceremony.company.block_rendering_enabled"
                                            :href="canDownloadBlockRender(block) ? block.render_url : null"
                                            :download="block.name + '.mp4'"
                                            target="_blank"
                                            class="mr-2 action-item"
                                            :class="{ 'disabled-block-download': !canDownloadBlockRender(block) }"
                                            v-tooltip="canDownloadBlockRender(block) ? $t('blocks.download_block_as_video') : $t('blocks.download_block_as_video_not_available')"
                                        >
                                            <icon name="download" />
                                        </a>

                                        <a href="#" class="action-item" v-tooltip="$t('blocks.delete_block_title')"
                                           @click="toggleDeleteModal(block)"
                                           v-if="(companySettings.can_delete_inloop_uitloop_block ? true : !isInloopUitloopBlock(block)) && !isCondolenceBlock(block)">
                                            <icon name="trash"></icon>
                                        </a>
                                        <a href="#" class="action-item" v-tooltip="$t('blocks.edit_block')"
                                           @click="toggleEditModal(block)">
                                            <icon name="pencil-alt"></icon>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="mt-3">
                            <block-info :ceremony="block.ceremony" v-if="block && block.ceremony" :class="'ml-0'"
                                        :block="block" />
                        </div>

                        <div class="block-comment" v-if="block.comment">{{ block.comment }}</div>

                        <div class="custom-control custom-switch" style="margin-top: 15px;" v-if="canRepeatMedia(block)"
                             @change="repeatBlock">
                            <input type="checkbox" class="custom-control-input" id="repeat_content"
                                   style="cursor: pointer" v-model="settings_request.repeat_audio">
                            <label class="custom-control-label" for="repeat_content">{{ $t('blocks.repeat_media') }}</label>
                        </div>

                        <div v-if="isCallBlock(block) && block.call_link">
                            <div class="alert alert-info">
                                {{ $t('blocks.call_link_info') }}
                            </div>

                            <a :href="block.call_link" target="_blank" class="call-link-box">
                                {{ block.call_link }}
                            </a>
                        </div>

                        <div v-if="isMediaBlock(block) || isInloopUitloopBlock(block) || isCondolenceBlock(block)">

                            <hr>

                            <div class="mt-1" v-if="block.files.length">
                                <h6 class="mb-2">{{ $t('blocks.music_in_block') }} <span v-if="block.files">({{
                                        block.files.length
                                    }})</span></h6>
                            </div>

                            <div class="mt-3" v-if="block.files.length">
                                <draggable @change="saveOrder(block)"
                                           :list="block.files"
                                           :disabled="isEditingFile"
                                           :handle="'.song-block-inner'"
                                           :group="{name: 'songs', put: ['library']}"
                                           :force-fallback="true">
                                    <transition-group class="row">
                                        <div class="col-12 mb-5" v-for="(file, fileIndex) of block.files"
                                             :key="file.id + '_' + fileIndex" v-if="isAudio(file)">
                                            <file-block
                                                :ceremony-code="code"
                                                :block="block"
                                                :file="file"
                                                :can-grab="true"
                                                @is-editing="isEditingFile = $event"
                                                @save="$store.dispatch('updateCeremony', $route.params.code)"
                                                @files-updated="filesUpdated(block, $event)"
                                                @unlink="block.files.splice(block.files.indexOf(file), 1); unlinkFile(block)"
                                            />
                                        </div>
                                    </transition-group>
                                </draggable>
                            </div>

                            <div class="custom-control custom-switch"
                                 v-if="block.files.length > 0 && (isInloopUitloopBlock(block) || isCondolenceBlock(block))"
                                 @change="saveBlockSettings()">
                                <input type="checkbox" class="custom-control-input" id="repeat_audio"
                                       style="cursor: pointer" v-model="settings_request.repeat_audio">
                                <label class="custom-control-label" for="repeat_audio">{{ $t('blocks.repeat_music') }}</label>
                            </div>

                            <hr class="my-3" v-if="block.files.length" />

                            <button class="btn btn-company btn-sm rounded-pill mb-1 add-music-button"
                                    @click="toggleMusicModal(block)">
                                <icon name="plus" scale="0.8"></icon>
                                {{ $t('blocks.add_music') }}
                            </button>
                        </div>
                    </div>
                </div>

            </div>


            <div class="col-12 col-lg-9">

                <div v-for="(sub_block, blockindex) in getSubBlocksForBlock(block)" style="position: relative">

                    <div style="position: absolute; z-index:50;left:-10px;top:-10px;">
                        <span class="badge badge-dark" v-if="isVideoBlock(sub_block)">{{ $t('blocks.video_block') }}</span>
                        <span class="badge badge-dark" v-if="isPhotoBlock(sub_block)">{{ $t('blocks.photo_block') }}</span>
                    </div>
                    <div class="card mb-3">
                        <!-- Card header -->
                        <div class="card-header pb-3">
                            <div class="d-flex justify-content-between align-items-center subblock-info">
                                <div class="subblock-info-title">
                                    <span class="h6" v-if="getBlockToUseForFiles(sub_block)">{{ getBlockToUseForFiles(sub_block).name }}</span>
                                    <span class="h6" v-else>{{ sub_block.name }} ({{ sub_block.files.length }} items)</span>
                                    <!--                                    <icon v-tooltip="'Blok instellingen'" name="cog" @click="toggleSubBlockModal(sub_block)" style="cursor:pointer;"></icon>-->

                                    <a style="margin-left: 5px;" href="#" class="action-item"
                                       v-tooltip="$t('blocks.delete_block')" @click="toggleDeleteModal(sub_block)"
                                       v-if="isSpeakerBlock(block) && blockindex > 0">
                                        <icon name="trash"></icon>
                                    </a>

                                    <block-info :show-name="false" :ceremony="block.ceremony"
                                                v-if="block && block.ceremony" :class="'ml-0'" :block="sub_block" />

                                </div>
                                <div class="text-right subblock-info-actions" v-if="!sub_block.use_files_from_block_id">
                                    <button class="btn btn-secondary btn-sm rounded-pill btn-icon show-more"
                                            @click="sub_block.max_files_to_show = sub_block.max_files_to_show === null ? 6 : null"
                                            v-if="sub_block.files.length > 6">
                                        <icon name="chevron-down" scale="0.8"
                                              v-if="sub_block.max_files_to_show > 6 || sub_block.max_files_to_show === null"></icon>
                                        <icon name="chevron-left" scale="0.8"
                                              v-if="sub_block.max_files_to_show !== null && sub_block.max_files_to_show <= 6"></icon>
                                        {{ $t('blocks.show') }} {{
                                            sub_block.max_files_to_show === null ? $t('blocks.show_less') : (sub_block.max_files_to_show <= 6 ? $t('blocks.show_more') : $t('blocks.show_all'))
                                        }}
                                    </button>

                                    <button class="btn btn-secondary btn-sm rounded-pill btn-icon "
                                            @click="toggleSubBlockModal(sub_block)">
                                        <icon name="cog" scale="0.8"></icon>
                                        {{ isVideoBlock(sub_block) ? $t('blocks.video_settings') : $t('blocks.photo_settings') }}
                                    </button>

                                    <button class="btn btn-icon btn-company btn-sm rounded-pill"
                                            v-show="ceremony.files_per_type[isVideoBlock(sub_block) ? 'video' : 'image'] > 0"
                                            @click="toggleLibraryModal(sub_block, 'image')">
                                        <icon name="plus" scale="0.8"></icon>
                                        {{ $t('blocks.select_from_library') }}
                                    </button>
                                </div>

                            </div>
                        </div>

                        <!-- Card body -->
                        <div class="card-body pt-0">
                            <!-- App logo + status -->
                            <div class="d-flex justify-content-between align-items-center">
                                <div>
                                    <div class="icon icon-sm">

                                    </div>
                                </div>

                            </div>

                            <div class="row">
                                <div class="col-12">

                                    <draggable @change="saveOrder(sub_block)"
                                               :disabled="isEditingFile"
                                               v-if="!sub_block.use_files_from_block_id"
                                               :list="sub_block.files"
                                               :group="{name: 'images', put: ['library']}"
                                               :force-fallback="true"
                                               :handle="'.file-block-inner'"
                                    >
                                        <transition-group>
                                            <div class="block-wrapper" v-for="(file, fileIndex) of sub_block.files"
                                                 :key="file.id + '_' + fileIndex"
                                                 v-if="fileIndex < sub_block.max_files_to_show || sub_block.max_files_to_show === null">
                                                <file-block
                                                    :ceremony-code="code"
                                                    :block="sub_block"
                                                    :file="file"
                                                    :can-grab="true"
                                                    :can-edit="!(fileIndex === 5 && sub_block.max_files_to_show <= 6 && sub_block.max_files_to_show !== null && sub_block.files.length > 6)"
                                                    @unlink="sub_block.files.splice(sub_block.files.indexOf(file), 1); unlinkFile(sub_block)"
                                                    @is-editing="isEditingFile = $event"
                                                    @save="$store.dispatch('updateCeremony', $route.params.code)"
                                                    @files-updated="filesUpdated(sub_block, $event)"
                                                    @file-state-changed="addToPreviewIfNotExists"
                                                    @click="showFilePreview(getFilesForType(sub_block), file)"
                                                >
                                                    <div v-tooltip="$t('blocks.show_more_tooltip')"
                                                         @click="sub_block.max_files_to_show = null; $event.stopPropagation()"
                                                         class="avatar avatar-more"
                                                         v-if="fileIndex === 5 && sub_block.max_files_to_show <= 6 && sub_block.max_files_to_show !== null && sub_block.files.length > 6"
                                                    >
                                                        {{ $t('blocks.show_remaining', { count: sub_block.files.length - 6 }) }}
                                                    </div>
                                                </file-block>
                                            </div>
                                        </transition-group>
                                    </draggable>

                                    <!-- foto block -->
                                    <dropzone-upload
                                        v-if="isPhotoBlock(sub_block) && !resetDropzone && !sub_block.use_files_from_block_id"
                                        :url="`/api/ceremonies/code/${code}/files/${sub_block.id}`"
                                        style="float: left; margin-right: 15px;"
                                        :extensions_text="getExtensionForBlock(sub_block)"
                                        :icon-only="true"
                                        :icon_text="$t('blocks.upload_photos')"
                                        :unique_number_per_dropzone="12"
                                        :max_files="250"
                                        :max_size_mb_per_file="15"
                                        :bestand="[]"
                                        :unique_string_per_dropzone="'subblock'"
                                        @uploaded="photoUploaded"
                                        @file_added="clearErrors(sub_block); sub_block.max_files_to_show = null"
                                        @dropzone_errors="setErrors(sub_block, $event)"
                                        @clear_dropzone_errors="clearErrors(sub_block)"
                                        @queueComplete="queueComplete('image', $event)">
                                    </dropzone-upload>
                                    <!-- end foto block -->


                                    <!-- video block -->
                                    <dropzone-upload
                                        :url="`/api/ceremonies/code/${code}/files/${sub_block.id}`"
                                        :extensions_text="getExtensionForBlock(sub_block)"
                                        style="float: left; margin-right: 15px;"
                                        :icon-only="true"
                                        :icon_text="$t('blocks.upload_video')"
                                        v-if="showVideoUpload(sub_block) && !sub_block.use_files_from_block_id && (sub_block.files.length === 0 || isTypeLedWall())"
                                        :unique_number_per_dropzone="12"
                                        :max_files="1"
                                        :max_size_mb_per_file="1500"
                                        :bestand="[]"
                                        :unique_string_per_dropzone="'subblock'"
                                        :chunking="true"
                                        @file_added="clearErrors(sub_block)"
                                        @chunks_merged="videoUploaded"
                                        @queueComplete="queueComplete('video', $event)"
                                        @dropzone_errors="setErrors(sub_block, $event)"
                                    >
                                    </dropzone-upload>
                                    <!-- video block -->

                                    <div v-if="getBlockToUseForFiles(sub_block)">
                                        <div class="block-wrapper" v-for="(file, fileIndex) of getBlockToUseForFiles(sub_block).files" :key="fileIndex">
                                            <file-block
                                                :ceremony-code="code"
                                                :block="sub_block"
                                                :file="file"
                                                :can-grab="false"
                                                :can-edit="false"
                                            />
                                        </div>
                                    </div>

                                    <div v-if="fixedImageBlock && fixedImageBlock.subblocks.length && canSelectFixedBlockInOtherBlocks" class="d-inline-block float-left">
                                        <div
                                            v-if="!sub_block.use_files_from_block_id && sub_block.type !== 2/*video*/"
                                            class="file-toggle-block"
                                            v-tooltip="$t('blocks.click_here_to_have_a_fixed_photo')"
                                            @click="saveUseFilesFromBlock(sub_block, fixedImageBlock.subblocks[0])">
                                            <div class="file-toggle-block-content">
                                                <span class="btn btn-secondary btn-sm btn-icon">{{ $t('blocks.use_fixed_photo') }}</span>
                                            </div>
                                        </div>
                                        <div
                                            v-if="sub_block.use_files_from_block_id"
                                            class="file-toggle-block"
                                            v-tooltip="$t('blocks.click_here_to_use_own_photos')"
                                            @click="saveUseFilesFromBlock(sub_block, null)"
                                        >
                                            <div class="file-toggle-block-content">
                                                <span class="btn btn-secondary btn-sm btn-icon">{{ $t('blocks.use_own_photos') }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="file_errors[sub_block.id] && file_errors[sub_block.id].length > 0">
                                <hr />

                                <div class="alert alert-danger alert-dismissible fade show" role="alert">
                                    <button @click="clearErrors(sub_block)" type="button" class="close"
                                            data-dismiss="alert" aria-label="Close"><span
                                        aria-hidden="true">&times;</span></button>
                                    <strong>{{ $t('blocks.problems_with_uploading_files') }}:</strong>
                                    <span class="d-block" v-for="error in file_errors[sub_block.id]">{{ error.name }} - {{
                                            error.error
                                        }}</span>
                                </div>
                            </div>

                            <div v-if="hasBlockErrors(sub_block)">
                                <hr />

                                <div class="alert alert-info alert-dismissible fade show" role="alert">
                                    <strong>{{ $t('blocks.wait_for_uploading_video_files') }}</strong>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                <div class="mb-3 clearfix">
                    <button class="btn btn-success btn-sm rounded-pill float-right ml-2"
                            @click="selected_block = null; fixedBlockCollapsed = false">
                        <icon name="times-circle" scale="0.8"></icon>
                        {{ $t('blocks.close_block') }}
                    </button>

                    <div v-if="getSubBlocksForBlock(block).length > 0">

                        <div class="float-right" style="position: relative"
                             :v-tooltip="getSubBlocksForBlock(block).length > 2 ? $t('blocks.max_subblocks_tooltip') : null">
                            <button v-if="isSpeakerBlock(block)" :disabled="getSubBlocksForBlock(block).length > 2"
                                    class="btn btn-secondary btn-sm rounded-pill"
                                    @click="show_subblock_options = !show_subblock_options">
                                <icon name="plus" scale="0.8"></icon>
                                {{ $t('blocks.add_block') }}
                            </button>

                            <div x-placement="top-start" :class="show_subblock_options ? 'show' : null"
                                 class="dropdown-menu-add-block dropdown-menu dropdown-menu-sm dropdown-menu-arrow p-3">
                                <a class="dropdown-item" @click="addSubBlock($event, 1)">
                                    <icon name="images"></icon>
                                    <span>{{ $t('blocks.photo_block') }}</span>
                                </a>
                                <a class="dropdown-item" @click="addSubBlock($event, 2)">
                                    <icon name="video"></icon>
                                    <span>{{ $t('blocks.video_block') }}</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>


        <block-edit-create :show_create_block_modal="show_create_block_modal" :after_block_id="after_block_id"
                           @close_create_block_modal="show_create_block_modal = null"
                           @block_saved="blockSaved($event); subBlockSaved();"></block-edit-create>

        <sub-block-edit v-if="edit_subblock" :edit_subblock="edit_subblock" @close_edit_subblock="edit_subblock = null"
                        :block="block" @load-block="blockSaved" @close="subBlockSaved"></sub-block-edit>


        <!-- Start delete modal -->
        <modal :show="show_delete_modal" :closable="!loading" @close="closeModal('show_delete_modal')">
            <div slot="header">
                <div class="modal-header">
                    <h5 class="modal-title">{{ $t('blocks.delete_block_title') }}</h5>
                    <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                        <span aria-hidden="true">×</span>
                    </button>
                </div>
            </div>

            <div slot="body">
                <div class="text-center" v-if="delete_loading">
                    <div class="spinner-border" role="status">
                        <span class="sr-only">{{ $t('general.loading') }}</span>
                    </div>
                </div>
                <p v-if="!delete_loading">
                    {{ $t('blocks.delete_block_message', { name: block_to_delete.name }) }}
                </p>
            </div>

            <div slot="footer">
                <button type="button" class="btn btn-warning btn-sm" @click="deleteBlock()">
                    {{ $t('blocks.delete_button') }}
                </button>
                <button type="button" class="btn btn-secondary btn-sm" data-dismiss="modal" @click="show_delete_modal = false">
                    {{ $t('general.close') }}
                </button>
            </div>
        </modal>
        <!-- End delete modal -->


        <!-- Start library modal -->
        <modal :show="show_library" :closable="!loading && !previewFiles.length" @close="closeModal('show_library')"
               :custom_modal_class="'fullwidth-modal'" :custom_dialog_css="'width:80vw; max-width: 1000px;'">
            <div slot="header">
                <div class="modal-header">
                    <h5 class="modal-title">
                        {{ $t('blocks.uploaded_files_title', { count: files.total }) }}
                    </h5>
                    <button type="button" class="close" data-dismiss="modal" aria-label="Close"
                            @click="closeModal('show_library')"><span aria-hidden="true">×</span></button>
                </div>
            </div>

            <div slot="body">

                <div class="row d-none d-sm-flex">
                    <div class="col-12 d-flex align-items-center" style="padding:0 30px; padding-bottom: 10px;">
                        <div>
                            <strong>{{ $t('general.sort_by') }}</strong>
                            <span v-for="librarySorting of librarySortings"
                                  class="sorting-item badge mr-1"
                                  :class="sortingField === librarySorting.field ? 'badge-info' : 'badge-light'"
                                  @click="updateFileSorting(librarySorting.field)"
                            >
                                {{ librarySorting.name }}
                                <icon scale="0.8" class="ml-1" name="sort-up"
                                      v-if="sortingField === librarySorting.field && sortingDirection === 'asc'"></icon>
                                <icon scale="0.8" class="ml-1" name="sort-down"
                                      v-else-if="sortingField === librarySorting.field && sortingDirection === 'desc'"></icon>
                                <icon scale="0.8" class="ml-1" name="sort" v-else></icon>
                            </span>
                        </div>

                        <input type="text" class="form-control form-control-sm float-right mr-3 ml-auto"
                               style="width:auto" v-model="filtering.name" @keyup.enter="getFiles()"
                               :placeholder="$t('blocks.search_files_placeholder')">
                        <button class="btn btn-sm btn-info" style="width: 60px;">
                            <icon name="search"></icon>
                        </button>
                    </div>

                    <div class="col-12" v-if="show_library === 'image' && files.data.length > 1" style="padding:0 30px;">
                        <span
                            class="badge badge-light pointer-cursor"
                            @click="toggleAllFilesChecked"
                        >
                            {{ allFilesSelected ? $t('blocks.deselect_all_files') : $t('blocks.select_all_files') }}
                        </span>
                    </div>
                </div>

                <div class="card-body" style="max-height: 70vh;overflow-y: scroll">

                    <!--                    <div class="alert alert-info">Selecteer één of meerdere bestanden om deze toe te voegen aan het blok.</div>-->

                    <div v-if="code">
                        <div class="row">
                            <div class="col-sm-4 col-xs-1 col-md-3 col-lg-2 mb-2" v-for="(file, index) in files.data">
                                <file-block
                                    :file="file"
                                    :ceremony-code="code"
                                    :can-select="true"
                                    :show-count="true"
                                    @save="addFileFromLibrary($event, index)"
                                    @select-file="file.checked = !file.checked"
                                    @delete="deleteFileFromLibary"
                                    @file-state-changed="addToPreviewIfNotExists"
                                    @click="showFilePreview(files, file)"
                                />
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            <div slot="footer" class="d-flex w-100 align-items-start">
                <pagination :pagination="files" @change="getFiles" />
                <button type="button" class="btn btn-secondary btn-sm ml-auto d-none d-sm-flex" data-dismiss="modal"
                        @click="closeModal('show_library')">
                    {{ $t('general.close') }}
                </button>
                <button type="button" class="btn btn-company btn-sm" @click="addFilesToBlock()"
                        :disabled="numberOfSelectedFiles() <= 0">
                    <icon name="plus"></icon>
                    {{ $t('blocks.add_selected_files', { count: numberOfSelectedFiles() }) }}
                </button>
            </div>
        </modal>
        <!-- End library modal -->


        <!-- Start music lib modal -->
        <modal :show="show_music_library" :closable="!loading" @close="closeModal('show_music_library')"
               :custom_modal_class="'fullwidth-modal music-library-modal'"
               :custom_dialog_css="'width:90vw; max-width: 1600px; max-height:80vh'"
               :custom_body_css="'padding-top: 0'">
            <div slot="header">
                <div class="modal-header music-library-header">
                    <h5 class="modal-title">Muziek bibliotheek <span v-if="files.length">({{ files.length }})</span>
                    </h5>

                    <button type="button" style="margin-left: 0" class="close d-inline d-sm-none" data-dismiss="modal"
                            aria-label="Close" @click="closeModal('show_music_library')"><span
                        aria-hidden="true">×</span></button>

                    <div class="row ml-auto music-library-actions">
                        <div class="col-12">
                            <button type="button" class="float-left btn btn-xs"
                                    :class="(music_library_select === 'upload') ? 'btn-outline-dark' : 'btn-dark'"
                                    @click="music_library_select = 'library'">
                                {{ $t('blocks.select_from_library') }}
                            </button>
                            <button type="button" class="float-left btn btn-xs"
                                    :class="(music_library_select === 'library') ? 'btn-outline-dark' : 'btn-dark'"
                                    @click="music_library_select = 'upload'">
                                {{ $t('blocks.upload_music') }}
                            </button>
                        </div>
                    </div>

                    <button type="button" style="margin-left: 0" class="close d-none d-sm-inline" data-dismiss="modal"
                            aria-label="Close" @click="closeModal('show_music_library')"><span
                        aria-hidden="true">×</span></button>
                </div>
            </div>

            <div slot="body" :style="music_library_select === 'upload' ? 'text-align: center' : null">
                <div v-if="code && music_library_select === 'upload'">
                    <dropzone-upload
                        :url="`/api/ceremonies/code/${code}/files/${block.id}`"
                        :extensions_text="'.mp3,.wav,.wma,.flac,.aac,.ac3,.m4a,.opus'"
                        :icon-only="true"
                        :icon_text="$t('blocks.upload_music_button')"
                        :unique_number_per_dropzone="3"
                        :max_files="100"
                        :max_size_mb_per_file="150"
                        :bestand="[]"
                        :timeout="600000"
                        :unique_string_per_dropzone="'musicblock'"
                        @uploaded="musicUploaded"
                        @queueComplete="queueComplete('music', $event)"
                        @dropzone_errors="setErrors({id: 'audio_upload'}, $event)"
                        @clear_dropzone_errors="clearErrors({id: 'audio_upload'})"
                        class="music-library-upload"
                        @song_added="songAdded"
                        :selected-block="selected_block"
                    />

                    <div v-if="file_errors.audio_upload && file_errors.audio_upload" class="alert alert-danger" role="alert">
                        <strong>{{ $t('blocks.music_upload_error') }}</strong>
                        <span class="d-block" v-for="error in file_errors.audio_upload">
                    {{ error.name }} - {{ error.error }}
                </span>
                    </div>
                </div>

                <song-library
                    v-if="show_music_library && music_library_select === 'library'"
                    :song-url="'/api/ceremonies/code/' + ceremony.code + '/songs'"
                    @song_added="songAdded"
                />
            </div>

            <div slot="footer">
                <button type="button" class="btn btn-secondary btn-sm d-none" data-dismiss="modal"
                        @click="closeModal('show_music_library')">
                    {{ $t('general.close') }}
                </button>
                <button type="button" class="btn btn-secondary btn-sm d-sm-none">
                    {{ $t('general.search') }}
                </button>
            </div>
        </modal>
        <!-- End music library modal -->
    </div>
</template>

<script>

    import Modal from '../../components/Modal';
    import FileBlock from '../../components/FileBlock';
    import { mapState } from 'vuex';
    import DropzoneUpload from '../../components/DropzoneUpload';
    import FullscreenLoading from '../../components/FullscreenLoading';
    import SongSearch from '../../components/SongSearch';
    import draggable from 'vuedraggable';
    import { getTotalDurationForSongFiles } from '../../utils/duration';
    import FileMixin from '../../mixins/FileMixin';
    import filteringFromObject from '../../utils/filteringFromObject';
    import FileStateCheckMixin from '../../mixins/FileStateCheckMixin';
    import FilePreviewMixin from '../../mixins/FilePreviewMixin';
    import BlockMixin from '../../mixins/BlockMixin';
    import FullscreenSlider from '../../components/FullscreenSlider';
    import BlockTimeline from './BlockTimeline';
    import UtilMixin from '../mixins/UtilMixin';
    import BlockInfo from '../../components/BlockInfo';
    import BlockEditCreate from './BlockEditCreate';
    import Preview from './Preview';
    import SubBlockEdit from './SubBlockEdit';
    import Pagination from '../../components/Pagination';
    import DownloadCeremony from './DownloadCeremony.vue';
    import * as Sentry from '@sentry/vue';
    import BlockTimelineRenderStateIcon from './BlockTimelineRenderStateIcon.vue';
    import { canDownloadBlockRender } from '../../utils/block.js';

    const defaultSorting = 'name,asc';

    export default {

        name: 'Blocks',

        components: {
            BlockTimelineRenderStateIcon,
            DownloadCeremony,
            Pagination,
            SubBlockEdit,
            BlockEditCreate,
            BlockInfo,
            BlockTimeline,
            FullscreenLoading,
            DropzoneUpload,
            Modal,
            SongSearch,
            draggable,
            FileBlock,
            FullscreenSlider,
            Preview,
        },

        mixins: [
            UtilMixin,
            FileMixin,
            FileStateCheckMixin,
            FilePreviewMixin,
            BlockMixin,
        ],

        data() {
            return {
                isEditingFile: false,
                code: null,
                selected_block: null,
                block_id: null,
                after_block_id: null,
                show_create_block_modal: null,
                edit_subblock: null,
                show_subblock_options: false,
                selected_block_type: 1,
                block_to_edit: {},
                block_to_delete: {},
                block_to_attach_files: {},
                file_errors: {},
                show_delete_modal: false,
                show_edit_modal: false,
                show_library: null,
                show_music_library: false,
                music_library_select: 'library',
                show_photo_dropzone: false,
                show_music_dropzone: false,
                show_video_dropzone: false,
                delete_loading: false,
                files: [],
                loading: false,
                sorting: defaultSorting,
                filtering: {},
                block: {
                    ceremony: null,
                    files: [],
                    subblocks: [],
                },
                request: {},
                settings_request: {
                    repeat_audio: false,
                },
                formData: {
                    name: null,
                    location_id: null,
                    date: null,
                    duration: null,
                    attendee: {
                        name: null,
                        email: null,
                    },
                },
                resetDropzone: false,
                librarySortings: [
                    {
                        name: this.$t('general.name'),
                        field: 'name',
                    },
                    {
                        name: this.$t('general.uploaded'),
                        field: 'created_at',
                    },
                    {
                        name: this.$t('general.used'),
                        field: 'blocks_count',
                    },
                ],

                fixedBlockCollapsed: false,
            };
        },

        computed: {
            ...mapState({
                locations: state => state.locations,
                ceremony: state => state.ceremony,
                timelineCeremony: state => state.timelineCeremony,
                user: state => state.user,
                fullscreenLoading: state => state.loading,
                companySettings: state => state.companySettings,
            }),

            embed() {
                return window.location.search.includes('embed=true');
            },

            totalDurationForSongFiles() {
                if (!this.block) {
                    return 0;
                }

                return getTotalDurationForSongFiles(this.block.files);
            },

            sortingField() {
                return this.sorting ? this.sorting.split(',')[0] : null;
            },

            sortingDirection() {
                return this.sorting ? this.sorting.split(',')[1] : null;
            },

            noBlockSelectedText() {
                return this.companySettings?.no_block_selected_text || this.$t('blocks.select_block_message');
            },

            canSelectFixedBlockInOtherBlocks() {
                return this.companySettings?.can_select_fixed_block_in_other_blocks;
            },

            fixedImageBlock() {
                return this.ceremony.blocks.find(block => {
                    return block.settings.find(setting => {
                        return setting.name === 'fixed_block' && Number(setting.value) === 1;
                    });
                });
            },

            allFilesSelected() {
                return (this.files?.data || []).every(file => file.checked);
            },
        },

        async created() {
            this.code = this.$route.params.code !== 'create' ? this.$route.params.code : null;
            this.block_id = this.$route.params.block_id !== 'create' ? this.$route.params.block_id : null;

            if (!(await this.checkCeremony())) {
                return;
            }

            // not checking for a ceremony causes an infinite async?
            if (this.code && !this.ceremony) {
                this.$store.dispatch('getCeremony', this.code);
            }
        },

        destroyed() {
            this.$store.dispatch('resetCeremony');
            window.removeEventListener('mousedown', this.windowMouseDown);
        },

        watch: {
            block: {
                deep: true,
                handler(newValue, oldValue) {

                    const block_change = (newValue && oldValue && newValue.id !== oldValue.id);
                    const subblock_change = (newValue && oldValue && newValue.subblocks[0] && oldValue.subblocks[0] && newValue.subblocks[0].type !== oldValue.subblocks[0].type);

                    if (block_change || subblock_change) {
                        this.resetDropzone = true;
                    }
                },
            },

            resetDropzone() {
                if (this.resetDropzone) {
                    this.file_errors = {};
                }
            },
        },

        methods: {
            canDownloadBlockRender,
            filesUpdated(block, files) {
                files.forEach(file => {
                    const fileIndex = block.files.findIndex(fileToFind => {
                        return fileToFind.id === file.id || fileToFind.id === file.original_file_id;
                    });

                    if (fileIndex === -1) {
                        return;
                    }

                    block.files[fileIndex] = file;
                });
            },

            showVideoUpload(sub_block) {
                if (this.isVideoBlock(sub_block) && !this.resetDropzone) {
                    if (this.isTypeLedWall()) {
                        return true;
                    }

                    return true;
                }
            },

            isTypeLedWall() {
                return this.block.ceremony.type === 'ledwall';
            },

            windowMouseDown(event) {
                const path = event.path || (event.composedPath && event.composedPath());

                if (path.map(pathItem => pathItem.className).indexOf('dropdown-item') !== -1) {
                    return;
                }

                this.show_subblock_options = false;
            },

            getSubBlocksForBlock(block) {
                if (this.isFixedBlock(block)) {
                    return [];
                }

                return block.subblocks.filter(subBlock => {
                    return subBlock.id && subBlock.type !== 4/*geen media*/;
                });
            },

            async checkCeremony() {
                this.loading = true;

                try {
                    await this.$http.get('/api/ceremonies/code/' + this.code);

                    this.loading = false;

                    return true;
                } catch (exception) {
                    Sentry.captureException(exception);
                    this.$router.push('/code?message=Code is niet meer beschikbaar');
                }

                return false;
            },

            previewCeremony() {
                this.$store.commit('showCeremonyPreview', this.ceremony);
            },

            async unlinkFile(selected_block) {
                this.getBlock(this.selected_block, false);
                this.$store.dispatch('updateCeremony', this.$route.params.code);
            },

            async getBlock(selected_block, loading = true) {
                if (!selected_block) {
                    return;
                }

                this.loading = loading;

                try {
                    const response = await this.$http.get(`/api/ceremonies/code/${this.code}/blocks/${selected_block.id}`);

                    const block = response.data;

                    this.setBlock(block);

                    this.request = response.data;

                } catch (exception) {
                    console.log(exception);
                    console.log('Cannot load ceremony');
                }

                this.loading = false;
            },

            log: function(evt) {
                window.console.log(evt);
            },

            updateFileSorting(field) {
                if (field === this.sortingField) {
                    if (this.sortingDirection === 'desc') {
                        this.sorting = null;
                    } else if (this.sortingDirection === 'asc') {
                        this.sorting = field + ',desc';
                    }
                } else {
                    this.sorting = field + ',asc';
                }

                this.getFiles();
            },

            async getFiles(page = undefined) {
                try {
                    const response = await this.$http.get(`/api/ceremonies/code/${this.code}/files`, {
                        params: {
                            page: page || this.files.current_page,
                            type: this.show_library,
                            filters: filteringFromObject(this.filtering),
                            order: this.sorting,
                        },
                    });

                    response.data.data = response.data.data.map(function(file) {
                        file.checked = false;

                        return file;
                    });

                    this.files = response.data;

                } catch (exception) {
                    console.log(exception);
                    console.log('Cannot load files');
                }
            },


            getFilesForType(block) {
                if (this.isVideoBlock(block)) {
                    return block.files.filter(file => this.isVideo(file));
                } else if (this.isPhotoBlock(block)) {
                    return block.files.filter(file => this.isImage(file));
                }
            },

            fixedBlock() {
                if (!this.ceremony) {
                    return [];
                }

                return this.ceremony.blocks.find(block => {
                    return block.settings.find(setting => setting.name === 'fixed_block' && setting.value);
                });
            },

            getFixedPhoto() {

                const fixedBlock = this.ceremony.blocks.find(block => {
                    return block.settings.find(setting => setting.name === 'fixed_block' && setting.value);
                });

                if (!fixedBlock) {
                    return false;
                }

                const fixedPhoto = fixedBlock.subblocks[0].files[0];

                if (!fixedPhoto) {
                    return false;
                }

                return fixedPhoto;
            },


            removeFixedPhoto() {
                this.fixedBlock().subblocks[0].files.splice(0, 1);
            },

            blockSelected(block) {
                this.fixedBlockCollapsed = true;
                this.selected_block = block;
                this.resetDropzone = true;

                if (block.id) {
                    this.getBlock(block);
                }
            },

            getExtensionForBlock(subblock) {
                if (subblock.type === 1) {
                    return '.png,.jpg,.jpeg,.gif,.heic,.webp';
                } else if (subblock.type === 2) {
                    return '.mp4,.webm,.avi,.wmv,.mov,.flv,.avi,.mkv,.mpg,.mpeg';
                }
            },

            /**
             * Check if we are ablo to drag an image into music box and vice versa.
             * @param event
             * @param data
             * @returns {boolean}
             */
            checkMove(event, data) {


                if (parseInt(event.draggedContext.element.type) === 1 /* image */ && event.relatedContext.component.$attrs.group.name === 'songs') {
                    return false;
                } else if (parseInt(event.draggedContext.element.type) === 2 /* song */ && event.relatedContext.component.$attrs.group.name === 'images') {
                    return false;
                }
            },


            async saveOrder(sub_block) {
                $('.tooltip').removeAttr('aria-hidden'); // Hide the tooltip

                const new_order = sub_block.files.map(function(file) {
                    return file.id;
                });

                try {
                    await this.$http.post(`/api/ceremonies/code/${this.code}/blocks/${sub_block.id}/save-order`, new_order);

                } catch (exception) {
                    console.log(exception);
                    console.log('Cannot save order');
                }
            },

            createNewBlock(after_block_id) {
                this.show_create_block_modal = { action: 'new' };
                this.after_block_id = after_block_id;
            },

            async repeatBlock() {
                try {
                    const repeat = this.settings_request.repeat_audio;

                    await this.$http.post(`/api/ceremonies/code/${this.ceremony.code}/blocks/${this.block.id}/save-settings`, {
                        repeat_audio: repeat,
                    });

                    await this.$http.post(`/api/ceremonies/code/${this.ceremony.code}/blocks/${this.block.subblocks[0].id}/save-settings`, {
                        repeat_photos: repeat,
                    });

                    this.$store.dispatch('updateCeremony', this.$route.params.code);
                    this.getBlock(this.selected_block, false); // update block info

                } catch (exception) {
                    console.log(exception);
                    console.log('Cannot save block settings');
                }
            },

            async saveBlockSettings() {

                try {
                    await this.$http.post(`/api/ceremonies/code/${this.ceremony.code}/blocks/${this.block.id}/save-settings`, this.settings_request);
                    this.$store.dispatch('updateCeremony', this.$route.params.code);
                    this.getBlock(this.selected_block, false); // update block info

                } catch (exception) {
                    console.log(exception);
                    console.log('Cannot save order');
                }
            },

            toggleDeleteModal(block) {
                this.block_to_delete = block;
                this.show_delete_modal = true;
            },

            toggleEditModal(block) {
                block.edit = true;
                block.subblock_type = null;
                this.show_create_block_modal = block;
            },

            toggleSubBlockModal(subblock) {
                this.edit_subblock = subblock;
            },

            toggleLibraryModal(block, type) {
                this.block_to_attach_files = block;

                if (this.isVideoBlock(block)) {
                    type = 'video';
                } else if (this.isPhotoBlock(block)) {
                    type = 'image';
                }

                this.sorting = defaultSorting;
                this.show_library = type;
                this.files = {
                    current_page: 1,
                };

                this.getFiles();
            },

            toggleMusicModal(block) {
                this.block_to_attach_files = block;
                this.show_music_library = true;
                this.music_library_select = 'library';
            },

            deleteFileFromLibary(file) {
                this.files.data.splice(this.files.data.indexOf(file), 1);

                // beter een splice
                this.getBlock(this.selected_block);
            },

            addFileFromLibrary(file, index) {
                this.files.data[index] = file;
            },

            async saveBlock(block, method = 'post') {
                this.loading = true;

                let block_id = '';

                if (method === 'put') {
                    block_id = '/' + block.id;
                }

                try {
                    const response = await this.$http[method](`/api/ceremonies/code/${this.code}/blocks${block_id}`, block);

                    this.setBlock({
                        ...this.block,
                        subblocks: [
                            ...this.getSubBlocksForBlock(this.block),
                            response.data,
                        ],
                    });

                    this.show_modal = false;
                } catch (exception) {
                    console.log(exception);
                    console.log('Cannot save block');
                }

                this.loading = false;
            },

            async addSong(songId) {
                try {
                    const response = await this.$http.post(`/api/ceremonies/code/${this.code}/files/${this.block.id}/song/${songId}`);

                    this.block.files.push(response.data);
                    // this.getBlock(this.block);
                } catch (exception) {
                    console.log(exception);
                    console.log('Cannot add song to block');
                }
            },


            /**
             *  I add images to a block
             */
            async addFilesToBlock() {

                const subblock = this.getSubBlocksForBlock(this.block).find((sub_block) => {
                    if (sub_block.id === this.block_to_attach_files.id) {
                        return sub_block;
                    }
                });

                this.files.data.filter((file) => {
                    if (file.checked === true) {
                        subblock.files.push(file);
                    }
                });

                await this.saveOrder(subblock);
                this.show_library = null;

                // reset all checks
                this.files.data.map((file) => {
                    file.checked = false;
                });

                this.getBlock(this.selected_block, false);
                this.$store.dispatch('updateCeremony', this.$route.params.code);
            },


            /**
             *  I trigger on the event and add a song
             * @param song
             */
            async songAdded(song) {
                await this.addSong(song.id);

                this.$store.dispatch('updateCeremony', this.$route.params.code);

                this.getBlock(this.selected_block, false); // update block info

                this.show_music_library = false;
            },

            addSubBlock(event, type) {
                this.request.subblocks.push({
                    name: 'blok',
                    type: type,
                    parent_id: this.block.id,
                    edit: true,
                    files: [],
                });

                const subBlock = {
                    name: 'blok',
                    type: type,
                    duration: 1,
                    order: 1,
                    parent_id: this.block.id,
                };

                this.saveBlock(subBlock);
                this.show_subblock_options = false;
            },

            async deleteBlock() {

                this.delete_loading = true;

                try {
                    await this.$http.delete(`/api/ceremonies/code/${this.code}/blocks/${this.block_to_delete.id}`);

                    this.show_delete_modal = false;

                    this.$store.dispatch('updateCeremony', this.$route.params.code);

                    if (this.block_to_delete.parent_id === null) {
                        this.selected_block = null;
                        this.fixedBlockCollapsed = false;
                    }

                    this.getBlock(this.selected_block);
                    this.block_to_delete = {};

                } catch (exception) {
                    console.log(exception);
                }

                this.delete_loading = false;

            },

            editBlock(block) {
                this.request = block;
            },

            showCard(block) {
                if (!this.request.id) {
                    return true;
                } else if (this.request.id === block.id) {
                    return true;
                }
                return false;
            },


            closeModal(type) {
                this[type] = false;
            },

            filesAdded(files) {
                console.log(files);
            },


            numberOfSelectedFiles() {
                if (!this.files || !this.files.data) {
                    return [];
                }

                const selected_files = this.files.data.filter(function(file) {
                    return (file.checked === true);
                });

                return selected_files.length ?? 0;
            },


            fixedphotoUploaded(file, response) {

                this.fixedBlock().subblocks[0].files.push(response.file_model);

                this.$store.dispatch('updateCeremony', this.ceremony.code);
            },

            photoUploaded(file, response) {

                const currentblock = this.getSubBlocksForBlock(this.block).find((block => {
                    return (block.id === response.file_model.block_id);
                }));

                currentblock.files.push(response.file_model);

                this.$store.dispatch('updateCeremony', this.ceremony.code);
            },

            videoUploaded(response) {
                const currentblock = this.getSubBlocksForBlock(this.block).find((block => {
                    return (block.id === response.file_model.block_id);
                }));

                if (currentblock) {
                    currentblock.files.push(response.file_model);
                }

                this.$store.dispatch('updateCeremony', this.ceremony.code);
                this.getBlock(this.selected_block, false);
            },

            musicUploaded(file, response) {
                this.block.files.push(response.file_model);

                // this.getBlock();
            },

            setErrors(block, error, flush = false) {
                if (!this.file_errors[block.id]) {
                    this.file_errors[block.id] = [];
                }

                this.file_errors[block.id].push(error);

                this.$forceUpdate();
            },

            clearErrors(block) {
                const errors = {
                    ...this.file_errors,
                };

                delete errors[block.id];

                this.file_errors = errors;
            },

            async blockSaved(block, update) {

                await this.$store.dispatch('updateCeremony', this.$route.params.code);

                if (!this.isFixedBlock(block)) {
                    this.selected_block = block;
                    this.setBlock({ ...this.block, ...block });
                    this.getBlock(this.selected_block, false); // update block info
                }
            },

            async subBlockSaved() {
                this.show_create_block_modal = null;
                this.edit_subblock = null;
            },

            setBlock(block) {
                block.subblocks = this.getSubBlocksForBlock(block).map((subblock) => {
                    if (this.block && this.block.id === block.id) {
                        const previousSubBlock = this.block.subblocks.find(subblockToFind => subblockToFind.id === subblock.id);

                        subblock.max_files_to_show = previousSubBlock ? previousSubBlock.max_files_to_show : 6;
                    } else {
                        subblock.max_files_to_show = 6;
                    }

                    subblock.edit = false;
                    return subblock;
                });

                const repeat_audio = this.getSettingByName('repeat_audio', block.settings || []);
                if (block.settings && repeat_audio) {
                    this.settings_request.repeat_audio = (parseInt(repeat_audio.value) === 1);
                } else {
                    this.settings_request.repeat_audio = false;
                }

                this.block = block;
                this.$nextTick(() => {
                    this.resetDropzone = false;
                });
            },


            queueComplete(type, state) {
                if (type === 'music' && this.file_errors.audio_upload && this.file_errors.audio_upload.length) {
                    this.getBlock(this.selected_block, false);

                    return;
                }

                if (type === 'music') {
                    this.show_music_library = false;
                }

                if (state === 'success') {
                    this.$notify({
                        group: 'default',
                        title: this.$t('blocks.files_uploaded_title'),
                        text: this.$t('blocks.files_uploaded_message'),
                        type: 'success',
                        duration: 2000,
                        position: 'top center',
                    });

                    this.getBlock(this.selected_block, false);
                }
            },

            showSubmittedModal() {
                if (this.user) {
                    return false;
                }

                return this.isSubmitted();
            },

            isSubmitted() {
                return (this.ceremony && this.ceremony.state_id === 2 /** submitted */);
            },

            hasBlockErrors(subBlock) {
                if (!subBlock.parent_block) {
                    return false;
                }

                if (!subBlock.files.some(file => file.state === 3/*3 = processing*/)) {
                    return false;
                }

                return !!this.ceremony.status.errors.find(error => {
                    return error.blocks.indexOf(subBlock.parent_block.id) !== -1;
                });
            },

            getBlockToUseForFiles(block) {
                return this.ceremony.blocks.reduce(((foundBlock, blockWithSubBlocks) => {
                    if (foundBlock) {
                        return foundBlock;
                    }

                    return blockWithSubBlocks.subblocks.find(blockToFind => blockToFind.id === block.use_files_from_block_id);
                }), null);
            },

            async saveUseFilesFromBlock(block, blockToUse) {
                this.loading = true;

                try {
                    await this.$http.post(`/api/ceremonies/code/${this.code}/blocks/${block.id}/use-block`, {
                        id: blockToUse?.id || null,
                    });

                    block.use_files_from_block_id = blockToUse?.id || null;
                } catch (exception) {
                    console.log(exception);
                    console.log('Cannot save block');
                }

                this.loading = false;
            },

            toggleAllFilesChecked() {
                if (!this.files?.data) {
                    return;
                }

                const checked = !this.allFilesSelected;
                this.files.data.forEach(file => file.checked = checked);
            },
        },

        async mounted() {
            document.addEventListener('mousedown', this.windowMouseDown);
        },
    };

</script>


<style lang="scss" scoped>
    .dropdown-item {
        cursor: pointer;
        font-size: 16px;
    }

    .alert-dismissible .close {
        top: 15px;
    }

    .fade-enter-active, .fade-leave-active {
        transition: opacity .5s;
    }

    .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */
    {
        opacity: 0;
    }

    .sortable-drag {
        .videoWrapper,
        .livevideoWrapper,
        .actions,
        .file-info,
        .btn-edit,
        .processing-info {
            display: none;
        }
    }

    .block-comment {
        font-style: italic;
        font-size: 14px;
        line-height: 14px;
        margin-top: 10px;
        margin-bottom: 20px;
    }

    .avatar-more {
        cursor: pointer;
        background-color: rgba(0, 0, 0, 0.3);
        top: 0;
        width: 100%;
        height: 100%;
        float: left;
        position: absolute;
        color: #FFF;
        text-align: center;
        font-size: 20px;
        line-height: 22px;
    }

    .dropdown-menu-add-block {
        bottom: calc(100% + 10px);
        top: auto;

        &.dropdown-menu-sm.dropdown-menu-arrow:before {
            bottom: 5px;
        }
    }

    .sorting-item {
        cursor: pointer;
        display: inline-flex;
        align-items: center;

        svg {
            margin-right: 0 !important;
        }
    }

    .block-wrapper {
        float: left;
        width: 115px;
        margin-right: 15px;
        position: relative;

        @media(max-width: 767px) {
            width: 50%;
            margin-right: 0;
            margin-bottom: 10px;

            &:nth-child(odd) {
                padding-right: 5px;
            }

            &:nth-child(even) {
                padding-left: 5px;
            }
        }
    }

    @media(max-width: 767px) {
        .subblock-info {
            flex-direction: column;

            .subblock-info-title {
                width: 100%;
                margin-bottom: 10px;
            }

            .subblock-info-actions {
                width: 100%;
                display: flex;
                flex-direction: column;

                button {
                    width: 100%;
                    margin: 0;
                    margin-bottom: 5px;

                    &:last-of-type {
                        margin-bottom: 15px;
                    }
                }

                button.show-more {
                    display: none;
                }
            }
        }

        .add-music-button {
            width: 100%;
        }

        .music-library-header {
            display: block;

            h5 {
                float: left;
            }

            .music-library-actions {
                width: 100%;
                margin: 0;

                .col-12 {
                    padding: 0;
                    display: flex;
                    margin-top: 10px;

                    button {
                        float: none;
                        width: 100%;
                    }
                }
            }
        }
    }

    .music-library-upload {
        margin: 50px;

        @media(max-width: 767px) {
            padding: 15px;
            margin: 0;
        }
    }

    .fixed-block-wrapper {
        min-width: 300px;
        max-width: 300px;
        margin-right: 20px;

        .card-body {
            padding: 15px;
            padding-top: 5px;
            padding-bottom: 2px;

            h6 {
                margin-bottom: 3px;
            }

            .dropzone-wrapper-wrapper {
                margin-bottom: 12px;
                margin-top: 10px;
            }
        }

        &.collapsed {
            cursor: pointer;

            .upload-fixed-photo-text {
                margin-bottom: 5px;
            }
        }

        @media(max-width: 767px) {
            min-width: 0;
            max-width: none;
            margin-right: 0;
        }
    }

    .timeline-wrapper {
        display: flex;

        @media(max-width: 767px) {
            flex-direction: column;
        }
    }

    .call-link-box {
        border: 1px solid #c4ccd2;
        display: block;
        padding: 0.875rem 0.875rem;
        border-radius: 0.375rem;
        background: #eff2f7;
    }

</style>

<style lang="scss">

    .fixed-block-wrapper {
        .dropzone-wrapper-wrapper {
            display: block !important;

            .dropzone-wrapper {
                display: block !important;

                .dropzone-icon-only {
                    width: 100% !important;
                }
            }
        }

        .image-wrapper.file-block-inner {
            background-position: top center;
        }

        &.collapsed {
            .image-wrapper.file-block-inner:not(.auto-ratio) {
                padding-top: 60px;
            }
        }
    }

    .btn-compact {
        padding: 0.25rem 0.5rem;
        font-size: 0.875rem;
        line-height: 1.5;
        border-radius: 0.2rem;
    }

    .file-toggle-block {
        width: 115px;
        cursor: pointer;

        .file-toggle-block-content {
            position: relative;
            width: 100%;
            padding-top: 100%; /* 1:1 Aspect Ratio */
            background-size: cover;
            background-position: center center;
            border-radius: 0.375rem;

            span {
                position: absolute;
                top: 0;
                left: 0;
                display: flex;
                height: 100%;
                width: 100%;
                justify-content: center;
                align-items: center;
                text-align: center;
            }
        }
    }

    .embed-block-content-wrapper {
        padding: 0 15px;
    }

    .fixed-image-block-render-state {
        position: absolute;
        right: 6px;
        bottom: 0;
    }

    a.disabled-block-download {
        cursor: not-allowed;

        svg {
            fill: #c5c9cf;
        }
    }

</style>
